/**
 * SyncStatusIndicator Component
 *
 * - Offline: centered modal with backdrop, blocks interaction.
 * - Online + pending submissions: bottom-right yellow sync box.
 */

import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useSettings } from '@/contexts/SettingsContext';
import { COMPONENT_SYNC_STATUS } from '@/constants/translations';
import { NetworkFailureModal } from './NetworkFailureModal';
import styles from './SyncStatusIndicator.module.css';

function formatRelativeTime(date: Date, t: typeof COMPONENT_SYNC_STATUS['ko']): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return t.justNow;
  if (diffMinutes < 60) return t.minutesAgo(diffMinutes);
  if (diffMinutes < 1440) return t.hoursAgo(Math.floor(diffMinutes / 60));
  return t.daysAgo(Math.floor(diffMinutes / 1440));
}

/**
 * Only active survey-taking pages should surface the "progress saved" reassurance.
 * Profile/history pages don't have in-flight answers, so showing it there misleads.
 */
function useIsInSurveyFlow(): boolean {
  const { pathname } = useLocation();
  return /\/s\/[^/]+\/(survey|training-survey)\//.test(pathname);
}

/**
 * Full-screen overlay shown while the device is offline.
 * Retry pings /health — success dispatches `online` so the hook unmounts this.
 */
function OfflineOverlay({
  t,
  showSavedMessage,
}: {
  t: typeof COMPONENT_SYNC_STATUS['ko'];
  showSavedMessage: boolean;
}) {
  const handleRetry = useCallback(async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    try {
      const res = await fetch('/health', {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal,
      });
      if (res.ok) {
        window.dispatchEvent(new Event('online'));
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      clearTimeout(timeoutId);
    }
  }, []);

  return (
    <NetworkFailureModal
      ariaLabelledBy="offline-title"
      title={t.offlineTitle}
      description={showSavedMessage ? t.offlineSavedMessage : undefined}
      subDescription={t.offlineAutoMessage}
      retryLabel={t.reconnect}
      retryingLabel={t.reconnecting}
      retryFailedMessage={t.reconnectFailed}
      onRetry={handleRetry}
    />
  );
}

export function SyncStatusIndicator() {
  const { isOnline, isSyncing, pendingCount, lastSyncAt, syncAll } = useOfflineSync();
  const { connectionType } = useNetworkStatus();
  const { settings } = useSettings();
  const t = COMPONENT_SYNC_STATUS[settings.language];
  const inSurveyFlow = useIsInSurveyFlow();

  // TEMP: 1차 오프라인 오버레이 비활성화 — 2차 Submit-failed 방어 테스트용
  // if (!isOnline) {
  //   return <OfflineOverlay t={t} showSavedMessage={inSurveyFlow} />;
  // }
  void isOnline;
  void inSurveyFlow;
  void OfflineOverlay;

  if (pendingCount === 0) {
    return null;
  }

  return (
    <div className={styles.container} role="status" aria-live="polite">
      <div className={styles.syncNeededBox}>
        {isSyncing ? (
          <>
            <div className={styles.spinner} aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray="60"
                  strokeDashoffset="45"
                />
              </svg>
            </div>
            <span className={styles.statusText}>{t.syncing}</span>
          </>
        ) : (
          <>
            <div className={styles.statusIcon}>
              <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 000-1.5h-3.25V5z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <span className={styles.statusText}>{t.syncNeeded(pendingCount)}</span>
            <button
              type="button"
              className={styles.syncButton}
              onClick={syncAll}
              disabled={isSyncing}
              aria-label={`Sync ${pendingCount} pending submissions`}
            >
              {t.syncNow}
            </button>
          </>
        )}

        {connectionType && connectionType !== 'unknown' && (
          <div className={styles.connectionInfo}>
            {t.connectionLabel(connectionType === 'wifi' ? t.connectionWifi : t.connectionMobile)}
          </div>
        )}

        {lastSyncAt && !isSyncing && (
          <div className={styles.lastSyncInfo}>
            {t.lastSync(formatRelativeTime(lastSyncAt, t))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SyncStatusIndicator;
